/**
 * React Integration for Elastic Sheet Physics Engine
 * 
 * 提供 Context 和 Hooks 来将弹性床单物理引擎与 React 组件系统集成
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { 
  ElasticSheetEngine, 
  getElasticSheetEngine, 
  destroyElasticSheetEngine,
  DisplacementSample,
  Vec2 
} from './elasticSheet';

// ============================================================================
// Context
// ============================================================================

interface ElasticSheetContextValue {
  engine: ElasticSheetEngine | null;
  frameCount: number;
  sampleDisplacement: (x: number, y: number) => DisplacementSample;
  sampleMultiplePoints: (points: Vec2[]) => DisplacementSample[];
  mousePosition: Vec2 | null;
}

const defaultSample: DisplacementSample = {
  displacement: 0,
  gradientX: 0,
  gradientY: 0,
  normal: { x: 0, y: 0, z: 1 }
};

const ElasticSheetContext = createContext<ElasticSheetContextValue>({
  engine: null,
  frameCount: 0,
  sampleDisplacement: () => defaultSample,
  sampleMultiplePoints: () => [],
  mousePosition: null
});

// ============================================================================
// Provider
// ============================================================================

interface ElasticSheetProviderProps {
  children: React.ReactNode;
}

export function ElasticSheetProvider({ children }: ElasticSheetProviderProps) {
  const [frameCount, setFrameCount] = useState(0);
  const [mousePosition, setMousePosition] = useState<Vec2 | null>(null);
  const engineRef = useRef<ElasticSheetEngine | null>(null);

  useEffect(() => {
    // 获取或创建引擎实例
    const engine = getElasticSheetEngine();
    engineRef.current = engine;

    // 调整网格大小以适应视口
    const handleResize = () => {
      engine.resize(window.innerWidth, window.innerHeight);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    // 订阅物理更新
    const unsubscribe = engine.subscribe(() => {
      setFrameCount(engine.frameCount);
    });

    // 全局鼠标追踪
    const handleMouseMove = (e: MouseEvent) => {
      const pos = { x: e.clientX, y: e.clientY };
      engine.setMousePosition(pos);
      setMousePosition(pos);
    };

    const handleMouseLeave = () => {
      engine.setMousePosition(null);
      setMousePosition(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      unsubscribe();
      destroyElasticSheetEngine();
    };
  }, []);

  const sampleDisplacement = useCallback((x: number, y: number): DisplacementSample => {
    if (!engineRef.current) return defaultSample;
    return engineRef.current.sampleDisplacement(x, y);
  }, []);

  const sampleMultiplePoints = useCallback((points: Vec2[]): DisplacementSample[] => {
    if (!engineRef.current) return points.map(() => defaultSample);
    return engineRef.current.sampleMultiplePoints(points);
  }, []);

  const contextValue = useMemo(() => ({
    engine: engineRef.current,
    frameCount,
    sampleDisplacement,
    sampleMultiplePoints,
    mousePosition
  }), [frameCount, sampleDisplacement, sampleMultiplePoints, mousePosition]);

  return (
    <ElasticSheetContext.Provider value={contextValue}>
      {children}
    </ElasticSheetContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * 获取弹性床单上下文
 */
export function useElasticSheet() {
  return useContext(ElasticSheetContext);
}

/**
 * 为指定的元素采样位移场，返回变换参数
 */
export function useElasticTransform(ref: React.RefObject<HTMLElement | null>) {
  const { frameCount, sampleDisplacement, sampleMultiplePoints } = useElasticSheet();
  const [transform, setTransform] = useState({
    rotateX: 0,
    rotateY: 0,
    translateZ: 0,
    scale: 1,
    // 四角位移（用于非刚体效果可视化）
    corners: {
      topLeft: 0,
      topRight: 0,
      bottomLeft: 0,
      bottomRight: 0
    }
  });

  useEffect(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // 采样中心点
    const centerSample = sampleDisplacement(centerX, centerY);

    // 采样四个角点（用于非刚体变换）
    const cornerPoints: Vec2[] = [
      { x: rect.left, y: rect.top },           // topLeft
      { x: rect.right, y: rect.top },          // topRight
      { x: rect.left, y: rect.bottom },        // bottomLeft
      { x: rect.right, y: rect.bottom }        // bottomRight
    ];
    const cornerSamples = sampleMultiplePoints(cornerPoints);

    // 从梯度计算旋转角度
    // 梯度表示表面倾斜方向，转换为旋转角度
    const tiltScale = 0.8; // 调整倾斜灵敏度
    const rotateX = -centerSample.gradientY * tiltScale * 15; // 度
    const rotateY = centerSample.gradientX * tiltScale * 15;  // 度

    // 位移转换为 translateZ
    const translateZ = centerSample.displacement * 0.5;

    // 基于位移的微妙缩放
    const scale = 1 + centerSample.displacement * 0.002;

    setTransform({
      rotateX,
      rotateY,
      translateZ,
      scale,
      corners: {
        topLeft: cornerSamples[0]?.displacement ?? 0,
        topRight: cornerSamples[1]?.displacement ?? 0,
        bottomLeft: cornerSamples[2]?.displacement ?? 0,
        bottomRight: cornerSamples[3]?.displacement ?? 0
      }
    });
  }, [frameCount, ref, sampleDisplacement, sampleMultiplePoints]);

  return transform;
}

/**
 * 计算 CSS transform 字符串
 */
export function useElasticTransformStyle(ref: React.RefObject<HTMLElement | null>) {
  const transform = useElasticTransform(ref);

  const style = useMemo(() => ({
    transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) translateZ(${transform.translateZ}px) scale(${transform.scale})`,
    transformStyle: 'preserve-3d' as const,
    willChange: 'transform' as const
  }), [transform]);

  return { style, transform };
}

/**
 * 计算基于位移的阴影效果
 */
export function useElasticShadow(ref: React.RefObject<HTMLElement | null>) {
  const transform = useElasticTransform(ref);

  const shadow = useMemo(() => {
    const depth = Math.abs(transform.translateZ);
    const blur = 10 + depth * 2;
    const spread = depth * 0.5;
    const opacity = Math.min(0.3, 0.1 + depth * 0.01);
    
    // 基于倾斜方向偏移阴影
    const offsetX = transform.rotateY * 0.5;
    const offsetY = -transform.rotateX * 0.5;

    return `${offsetX}px ${offsetY}px ${blur}px ${spread}px rgba(0, 0, 0, ${opacity})`;
  }, [transform]);

  return shadow;
}
